import Swal from "sweetalert2";

import { AlertToast } from "@/helpers/alerts/AlertToast";

import { OrganizationModulesRequest } from "@/model/Modules/OrganizationModules";

type Callback = (request: OrganizationModulesRequest) => void;

export const Alert = (
	callback: Callback,
	isActive: boolean,
	moduleName: string,
) => {
	const request: OrganizationModulesRequest = {
		moduleName,
		activated: !isActive,
	};
	if (!isActive) {
		AlertSuccess(moduleName);
		callback(request);
	} else {
		AlertConfirm(callback, request);
	}
};

const AlertSuccess = (moduleName: string) => {
	Swal.fire({
		title: `Woohoo! You know have access to ${moduleName}!`,
		text: "Enjoy!",
		icon: "success",
	});
};

export const AlertConfirmDeleteComment = (
	callback: () => void,
	commentId: number,
) => {
	Swal.fire({
		title: "Are you sure?",
		text: `You want to delete this comment ${commentId}?`,
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete it!",
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};


const AlertConfirm = (
	callback: Callback,
	request: OrganizationModulesRequest,
) => {
	Swal.fire({
		title: `Are you sure you want to disable ${request.moduleName}?`,
		text: "You won't be able to revert this!",
		icon: "question",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, disable it!",
	}).then((result) => {
		if (result.isConfirmed) {
			callback(request);
			Swal.fire(
				"Disabled!",
				`Your ${request.moduleName} module has been disabled.`,
				"success",
			);
		}
	});
};

export const AlertWarning = (moduleName: string) => {
	Swal.fire({
		title: `${moduleName} is not available yet!`,
		text: "Contact SATOS for more information",
		icon: "warning",
	});
};

export const AlertConfirmStepOverride = (callback: () => void) => {
	Swal.fire({
		title: "Are you sure?",
		text: "By overriding this step you actively confirm that the values is out of boundary.",
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, override step!",
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};

export const AlertConfirmDelete = (callback: () => void, name: string) => {
	Swal.fire({
		title: "Are you sure?",
		text: `You want to delete ${name}`,
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete it!",
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};

export const AlertConfirmDeleteShiftEvent = (
	callback: () => void,
	shiftEventId: number,
) => {
	Swal.fire({
		title: "Are you sure?",
		text: `You want to delete this shift event ${shiftEventId}?`,
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete it!",
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};

export const AlertConfirmDeleteMeasurementEdit = (
	callback: () => void,
	measurementId: number,
) => {
	Swal.fire({
		title: "Are you sure?",
		text: `You want to delete ${measurementId} measurement point?`,
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete it!",
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};


export const AlertConfirmDeleteTask= (
	callback: () => void,
	isLightMode: boolean, // this returns true for light mode, false for dark mode
) => {
	const theme = isLightMode
		? { background: "#fff", color: "#000" }
		: { background: "#333", color: "#fff" };

	Swal.fire({
		title: "Are you sure?",
		text: "You will now delete a task",
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete task!",
		background: theme.background,
		color: theme.color,
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};

export const AlertConfirmCompleteTask= (
	callback: () => void,
	isLightMode: boolean, // this returns true for light mode, false for dark mode
) => {
	const theme = isLightMode
		? { background: "#fff", color: "#000" }
		: { background: "#333", color: "#fff" };

	Swal.fire({
		title: "Are you sure?",
		text: "You will now confirm a task",
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, confirm task!",
		background: theme.background,
		color: theme.color,
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};


export const AlertConfirmDeleteGroup = (
	callback: () => void,
	isLightMode: boolean, // this returns true for light mode, false for dark mode
) => {
	const theme = isLightMode
		? { background: "#fff", color: "#000" }
		: { background: "#333", color: "#fff" };

	Swal.fire({
		title: "Are you sure?",
		text: "You will now delete a group",
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete group!",
		background: theme.background,
		color: theme.color,
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};
export const AlertConfirmDeleteMeasurement = (
	callback: () => void,
	isLightMode: boolean, // this returns true for light mode, false for dark mode
) => {
	const theme = isLightMode
		? { background: "#fff", color: "#000" }
		: { background: "#333", color: "#fff" };

	Swal.fire({
		title: "Are you sure?",
		text: "You will now delete a measurement",
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete measurement!",
		background: theme.background,
		color: theme.color,
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};

export const AlertConfirmDeleteSample = (
	callback: () => void,
	isLightMode: boolean, // this returns true for light mode, false for dark mode
) => {
	const theme = isLightMode
		? { background: "#fff", color: "#000" }
		: { background: "#333", color: "#fff" };

	Swal.fire({
		title: "Are you sure?",
		text: "You will now delete a sample",
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete sample!",
		background: theme.background,
		color: theme.color,
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};
export const AlertConfirmDeleteAnalysis = (
	callback: () => void,
	isLightMode: boolean, // this returns true for light mode, false for dark mode
) => {
	const theme = isLightMode
		? { background: "#fff", color: "#000" }
		: { background: "#333", color: "#fff" };

	Swal.fire({
		title: "Are you sure?",
		text: "You will now delete an analysis",
		icon: "warning",
		showCancelButton: true,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete analysis!",
		background: theme.background,
		color: theme.color,
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};

export const AlertConfirmDeleteCourse = (callback: () => void) => {
	Swal.fire({
		title: "Do you want to save the changes?",
		showDenyButton: true,
		confirmButtonText: "Save",
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		denyButtonText: `Don't save`,
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};

export const AlertSnow = (callback: () => void) => {
	Swal.fire({
		title: "❄️ Let it Snow! 🎅",
		text: "Do you want to cover the screen in a magical snowstorm?",
		showDenyButton: true,
		showCancelButton: true,
		confirmButtonText: "🎄 Yes, let it snow! ❄️",
		denyButtonText: "⛄ No, I hate joy 😢",
		background: "#fefefe",
		color: "black",
		confirmButtonColor: "#4CAF50",
		denyButtonColor: "#E74C3C",
		cancelButtonColor: "#B3B3B3",
		customClass: {
			popup: "swal-popup-christmas",
			title: "swal-title-christmas",
		},
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success("❄️ It's snowing! Enjoy the festive vibes! 🎅");
			} else if (result.isDenied) {
				AlertToast.info("No snow? Bah humbug! 😞");
			}
		})
		.catch(() => {
			AlertToast.failed("Oops! Something went wrong. 🎄");
		});
};

export const AlertConfirmDeleteMedia = (callback: () => void) => {
	Swal.fire({
		title: "Do you want to save the changes?",
		showDenyButton: true,
		showCancelButton: false,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete media!",
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};

export const AlertConfirmDeleteQuestion = (callback: () => void) => {
	Swal.fire({
		title: "Do you want to save the changes?",
		showDenyButton: true,
		showCancelButton: false,
		confirmButtonColor: "#2e7d32",
		cancelButtonColor: "#af3f3b",
		confirmButtonText: "Yes, delete question!",
	})
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				AlertToast.success();
			}
		})
		.catch(() => {
			AlertToast.failed();
		});
};
